import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { z } from "zod/v4";
import { format, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "motion/react";
import {
  ChevronLeft,
  Star,
  MapPin,
  Sparkles,
  Award,
  Share2,
  Heart,
  Image as ImageIcon,
} from "lucide-react";
import { chefQueries } from "@/domain/chef/chef.query";
import { menuQueries } from "@/domain/menu/menu.query";
import { availabilityQueries } from "@/domain/availability/availability.query";
import { LiveChefMenusList } from "@/components/marketplace/chef-profile/LiveChefMenusList";
import { LiveBookingCard } from "@/components/marketplace/chef-profile/LiveBookingCard";
import { ChefLiveCard } from "@/components/marketplace/ChefLiveCard";
import { ChefCardSkeleton } from "@/components/ChefCardSkeleton";
import { Image } from "@/components/ui/image";
import { Button } from "@/components/ui/button";
import { CustomRequestStore } from "@/stores/customRequestStore";
import { createSEOHead, createChefSEO } from "@/lib/seo";
import { NotFound } from "@/components/NotFound";
import { WhatsIncluded } from "@/components/marketplace/chef-profile/WhatsIncluded";
import { ChefFAQ } from "@/components/marketplace/chef-profile/ChefFAQ";
import { QuoteConfigurator } from "@/components/marketplace/quote-configurator/QuoteConfigurator";
import { getImageUrl, getIpxImageUrl } from "@/lib/image";
import { cn } from "@/lib/utils";

const HERO_FALLBACK = "/images/find_chef_image.webp";
const STOCK_IMAGES = [
  "/images/menu_1.webp",
  "/images/menu_2.webp",
  "/images/menu_3.webp",
  "/images/menu_4.webp",
  "/images/menu_5.webp",
  "/images/menu_6.webp",
  "/images/menu_7.webp",
];

const chefSearchSchema = z.object({
  menuId: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute("/(public)/chefs/$chefId")({
  component: RouteComponent,
  validateSearch: (search) => {
    const result = chefSearchSchema.safeParse(search);
    return result.success ? result.data : {};
  },
  loader: async ({ context: { queryClient }, params }) => {
    const parts = params.chefId.split("-");
    const id = parts[parts.length - 1];

    const numericId = Number(id);
    if (isNaN(numericId) || numericId <= 0) {
      throw notFound();
    }

    const currentDate = format(new Date(), "yyyy-MM-dd");

    const [chef] = await Promise.all([
      queryClient.ensureQueryData(chefQueries.profile(numericId)),
      queryClient.ensureQueryData(menuQueries.publicChefMenus(numericId)),
      queryClient.ensureQueryData(
        availabilityQueries.publicChefAvailabilities(numericId, currentDate),
      ),
      queryClient.ensureQueryData(chefQueries.publicFeedbacks(numericId)),
    ]);

    return chef;
  },
  notFoundComponent: () => (
    <NotFound
      title="Chef non trouvé"
      message="Désolé, ce chef n'existe pas ou plus. Découvrez nos autres talents culinaires."
    />
  ),
  head: ({ loaderData }) => {
    const chef = loaderData;
    if (!chef)
      return createSEOHead({
        title: "Chef non trouvé",
        description: "Chef non trouvé",
      });
    return createSEOHead(createChefSEO(chef));
  },
  pendingComponent: () => (
    <div className="container mx-auto px-4 py-12">
      <div className="h-[480px] rounded-3xl bg-white border border-border animate-pulse" />
    </div>
  ),
});

function RouteComponent() {
  const chef = Route.useLoaderData();
  const params = Route.useParams();
  const { menuId: initialMenuId } = Route.useSearch();
  const { setOpen } = CustomRequestStore();
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const currentMonthDate = format(currentMonth, "yyyy-MM-dd");
  const bookingRef = useRef<HTMLDivElement | null>(null);
  const menusRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!initialMenuId) return;
    const node = menusRef.current;
    if (!node) return;
    node.scrollIntoView({ behavior: "auto", block: "start" });
  }, [initialMenuId]);

  const [quoteState, setQuoteState] = useState<{
    open: boolean;
    menuId: number | null;
    date: string | null;
    guests: number;
    pricePerPerson: number;
  }>({
    open: false,
    menuId: null,
    date: null,
    guests: 4,
    pricePerPerson: 0,
  });

  const { data: menus = [] } = useQuery(menuQueries.publicChefMenus(chef.id));
  const { data: availabilities = [] } = useQuery(
    availabilityQueries.publicChefAvailabilities(chef.id, currentMonthDate),
  );
  const { data: feedbacks = [] } = useQuery(
    chefQueries.publicFeedbacks(chef.id),
  );

  const { data: nearbyChefs, isLoading: isLoadingNearbyChefs } =
    useInfiniteQuery(chefQueries.findChefs({ city: chef.workingCity }));

  const otherChefs =
    nearbyChefs?.pages?.[0]?.data
      ?.filter((c) => c.id !== chef.id)
      ?.slice(0, 3) || [];

  const photos = buildPhotos(chef, menus);
  const prices = Array.from(
    new Set((menus || []).map((menu: any) => menu.price)),
  ).sort((a, b) => a - b);
  const priceFromCents = prices[0] ?? null;

  const ratingNumber = Number(chef.averageRating ?? 0);
  const reviewsCount = feedbacks.length;
  const bookingsCount = Number(chef.bookingsCount ?? 0);

  const scrollToBooking = () => {
    bookingRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  return (
    <div className="pb-24 lg:pb-12">
      <ChefHero
        chef={chef}
        photos={photos}
        rating={ratingNumber}
        reviewsCount={reviewsCount}
        bookingsCount={bookingsCount}
      />

      <section className="container mx-auto px-4 lg:px-6 pt-10 lg:pt-12">
        <div className="grid lg:grid-cols-[1fr_380px] gap-8 lg:gap-12">
          <div className="flex flex-col gap-14 min-w-0">
            <AboutChef
              chef={chef}
              bookingsCount={bookingsCount}
              rating={ratingNumber}
              reviewsCount={reviewsCount}
            />

            <div ref={menusRef} className="scroll-mt-24">
              <LiveChefMenusList
                menus={menus as any}
                chefFirstName={chef.firstName}
                initialMenuId={initialMenuId}
              />
            </div>

            <WhatsIncluded />

            {feedbacks.length > 0 && (
              <ChefReviewsSection
                chef={chef}
                rating={ratingNumber}
                feedbacks={feedbacks}
              />
            )}

            <ChefFAQ />

            <ZoneDeplacementCard
              firstName={chef.firstName}
              workingCity={chef.workingCity}
            />
          </div>

          <div ref={bookingRef} className="relative">
            <div className="lg:sticky lg:top-20">
              <LiveBookingCard
                chefFirstName={chef.firstName}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                availabilities={availabilities || []}
                menus={
                  menus?.map((menu: any) => ({
                    id: menu.id,
                    name: menu.name,
                    price: menu.price,
                  })) || []
                }
                onRequestQuote={(payload) =>
                  setQuoteState({
                    open: true,
                    menuId: payload.menuId,
                    date: payload.date,
                    guests: payload.guests,
                    pricePerPerson: payload.pricePerPerson,
                  })
                }
              />
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 lg:px-6 mt-20">
        <header className="mb-6">
          <h2 className="font-h3 text-dark-blue mb-1">Chefs aux alentours</h2>
          <div className="w-16 h-[2px] bg-primary rounded-full" />
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoadingNearbyChefs
            ? Array(3)
                .fill(0)
                .map((_, i) => <ChefCardSkeleton key={i} />)
            : otherChefs.map((c, i) => (
                <ChefLiveCard key={c.id} chef={c} index={i} />
              ))}
        </div>
      </section>

      <section className="container mx-auto px-4 lg:px-6 mt-14">
        <CustomRequestInlineCard
          firstName={chef.firstName}
          onClick={() => setOpen(true)}
        />
      </section>

      <MobileBookingBar
        firstName={chef.firstName}
        priceFromCents={priceFromCents}
        onClick={scrollToBooking}
      />

      <QuoteConfiguratorWrapper
        open={quoteState.open}
        onOpenChange={(open) =>
          setQuoteState((prev) => ({ ...prev, open }))
        }
        chefSlug={(chef as any).slug ?? params.chefId}
        chefFirstName={chef.firstName}
        menuId={quoteState.menuId}
        prefilledDate={quoteState.date}
        prefilledGuests={quoteState.guests}
        pricePerPerson={quoteState.pricePerPerson}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  QuoteConfigurator wrapper — adapts real menu data to the configurator
// ─────────────────────────────────────────────────────────────────────────

const DISH_TYPE_ORDER = [
  "PRESTARTER",
  "STARTER",
  "MAIN",
  "DESSERT",
  "SUPPLEMENT",
];
const DISH_TYPE_LABEL: Record<string, string> = {
  PRESTARTER: "Amuse-bouche",
  STARTER: "Entrée",
  MAIN: "Plat principal",
  DESSERT: "Dessert",
  SUPPLEMENT: "Supplément",
};

function QuoteConfiguratorWrapper({
  open,
  onOpenChange,
  chefSlug,
  chefFirstName,
  menuId,
  prefilledDate,
  prefilledGuests,
  pricePerPerson,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chefSlug: string;
  chefFirstName: string;
  menuId: number | null;
  prefilledDate: string | null;
  prefilledGuests: number;
  pricePerPerson: number;
}) {
  const { data: menuDetail } = useQuery({
    ...menuQueries.detail(menuId ?? 0),
    enabled: open && menuId !== null,
  });

  const adaptedMenu = useMemo(() => {
    if (!menuId || !menuDetail) return null;
    return adaptMenuForConfigurator(menuDetail);
  }, [menuId, menuDetail]);

  return (
    <QuoteConfigurator
      open={open}
      onOpenChange={onOpenChange}
      chefSlug={chefSlug}
      chefFirstName={chefFirstName}
      chefLastName=""
      prefilledDate={prefilledDate ?? ""}
      prefilledGuests={prefilledGuests}
      prefilledMenu={adaptedMenu}
      pricePerPerson={pricePerPerson}
    />
  );
}

function adaptMenuForConfigurator(menu: any): any {
  const dishes: Array<{
    name: string;
    description?: string | null;
    type: string;
  }> = menu.dishes ?? [];

  const grouped = new Map<string, typeof dishes>();
  dishes.forEach((d) => {
    const list = grouped.get(d.type) ?? [];
    list.push(d);
    grouped.set(d.type, list);
  });

  const courses = DISH_TYPE_ORDER.filter((t) => grouped.has(t)).map(
    (type) => ({
      type: DISH_TYPE_LABEL[type] ?? type,
      options: grouped.get(type)!.map((d) => ({
        title: d.name,
        description: d.description ?? "",
      })),
    }),
  );

  return {
    id: String(menu.id),
    name: menu.name,
    slug: "",
    image: menu.image ?? "",
    pricePerPerson: Math.round((menu.price ?? 0) / 100),
    courseCount: courses.length,
    durationHours: 2,
    minGuests: menu.minGuests ?? 1,
    maxGuests: menu.maxGuests ?? 50,
    rating: 0,
    reviewsCount: 0,
    description: menu.description ?? "",
    courses,
    cuisine: "",
    occasions: [],
    dietary: menu.tags ?? [],
    badges: [],
    chef: {} as any,
  };
}

// ─────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────

function buildPhotos(
  chef: { id: number; banner: string | null },
  menus: any[],
): string[] {
  const list: string[] = [];
  const fallbackBanner = STOCK_IMAGES[Math.abs(chef.id) % STOCK_IMAGES.length];

  if (chef.banner && chef.banner.trim() !== "") {
    list.push(getImageUrl(chef.banner) ?? fallbackBanner);
  } else {
    list.push(fallbackBanner);
  }

  (menus ?? []).forEach((menu: any) => {
    if (menu.image) {
      const url = getImageUrl(menu.image);
      if (url) list.push(url);
    }
  });

  // Pad with stock images so the gallery never looks empty
  let i = 0;
  while (list.length < 5 && i < STOCK_IMAGES.length) {
    if (!list.includes(STOCK_IMAGES[i])) {
      list.push(STOCK_IMAGES[i]);
    }
    i++;
  }

  return list.slice(0, 5);
}

function deriveBadges(rating: number, bookingsCount: number) {
  const badges: { label: string; icon: typeof Award }[] = [];
  if (rating >= 4.8 && bookingsCount >= 10) {
    badges.push({ label: "Coup de cœur", icon: Award });
  } else if (bookingsCount >= 20) {
    badges.push({ label: "Populaire", icon: Sparkles });
  } else if (bookingsCount === 0) {
    badges.push({ label: "Nouveau", icon: Sparkles });
  }
  return badges;
}

// ─────────────────────────────────────────────────────────────────────────
//  Hero
// ─────────────────────────────────────────────────────────────────────────

function ChefHero({
  chef,
  photos,
  rating,
  reviewsCount,
  bookingsCount,
}: {
  chef: any;
  photos: string[];
  rating: number;
  reviewsCount: number;
  bookingsCount: number;
}) {
  const [liked, setLiked] = useState(false);
  const main = photos[0] ?? HERO_FALLBACK;
  const thumbs = photos.slice(1, 5);
  const emptySlots = Math.max(0, 4 - thumbs.length);
  const badges = deriveBadges(rating, bookingsCount);
  const avatarSrc =
    getIpxImageUrl(chef.avatar, 150) ??
    `/images/avatar_${(Math.abs(chef.id) % 6) + 1}.webp`;

  return (
    <section className="container mx-auto px-4 lg:px-6 pt-6">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <Link
          to="/trouver-un-chef/chefs"
          className="flex items-center gap-1 hover:text-primary transition-colors font-medium"
        >
          <ChevronLeft size={14} />
          Tous les chefs
        </Link>
        {chef.workingCity && (
          <>
            <span>·</span>
            <span>{chef.workingCity}</span>
          </>
        )}
      </nav>

      <div className="relative grid grid-cols-1 lg:grid-cols-4 gap-2 rounded-3xl overflow-hidden h-[480px] lg:h-[520px]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="lg:col-span-2 relative h-full bg-background"
        >
          <Image
            src={main}
            alt={`Cuisine de ${chef.firstName}`}
            fallbackSrc={HERO_FALLBACK}
            layout="fullWidth"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark-blue/80 via-dark-blue/20 to-transparent" />
        </motion.div>

        <div className="hidden lg:grid lg:col-span-2 grid-cols-2 gap-2">
          {thumbs.map((photo, i) => (
            <motion.div
              key={`${photo}-${i}`}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, delay: 0.05 + i * 0.05 }}
              className="relative overflow-hidden bg-background"
            >
              <Image
                src={photo}
                alt={`Plat signé ${chef.firstName} — photo ${i + 2}`}
                fallbackSrc={HERO_FALLBACK}
                layout="fullWidth"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </motion.div>
          ))}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <div
              key={`placeholder-${i}`}
              className="relative overflow-hidden bg-salmon/30 flex items-center justify-center"
              aria-label="Photo à venir"
            >
              <div className="flex flex-col items-center gap-1 text-primary/60">
                <ImageIcon size={20} />
                <span className="text-[10px] uppercase tracking-wider font-semibold">
                  Photo à venir
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="absolute top-4 right-4 flex gap-2 z-10">
          <button
            type="button"
            onClick={() => setLiked((v) => !v)}
            className="w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm hover:bg-white shadow-sm flex items-center justify-center transition-colors"
            aria-label={liked ? "Retirer des favoris" : "Ajouter aux favoris"}
          >
            <Heart
              size={16}
              className={liked ? "text-primary fill-primary" : "text-dark-blue"}
            />
          </button>
          <button
            type="button"
            className="w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm hover:bg-white shadow-sm flex items-center justify-center transition-colors"
            aria-label="Partager"
          >
            <Share2 size={16} className="text-dark-blue" />
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="absolute left-4 lg:left-8 bottom-4 lg:bottom-8 right-4 lg:right-auto lg:max-w-xl z-10"
        >
          <div className="bg-white/95 backdrop-blur-md rounded-2xl p-5 shadow-xl flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-white shrink-0 bg-white">
              <Image
                src={avatarSrc}
                alt={`Portrait de ${chef.firstName}`}
                fallbackSrc={`/images/avatar_${(Math.abs(chef.id) % 6) + 1}.webp`}
                layout="constrained"
                width={64}
                height={64}
                className="object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              {badges.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {badges.map((b) => {
                    const Icon = b.icon;
                    return (
                      <span
                        key={b.label}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary text-white"
                      >
                        <Icon size={10} />
                        {b.label}
                      </span>
                    );
                  })}
                </div>
              )}
              <h1 className="font-h3 text-dark-blue leading-tight">
                {chef.firstName}
              </h1>
              <div className="flex items-center gap-3 mt-1 text-sm flex-wrap">
                {rating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star
                      size={13}
                      fill="currentColor"
                      className="text-primary"
                    />
                    <span className="font-bold text-dark-blue tabular-nums">
                      {rating.toFixed(1)}
                    </span>
                    {reviewsCount > 0 && (
                      <span className="text-muted-foreground">
                        ({reviewsCount} avis)
                      </span>
                    )}
                  </div>
                )}
                {chef.workingCity && (
                  <>
                    <span className="text-muted-foreground hidden sm:inline">
                      ·
                    </span>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <MapPin size={12} className="text-primary" />
                      {chef.workingCity}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  About
// ─────────────────────────────────────────────────────────────────────────

function AboutChef({
  chef,
  bookingsCount,
  rating,
  reviewsCount,
}: {
  chef: any;
  bookingsCount: number;
  rating: number;
  reviewsCount: number;
}) {
  return (
    <section>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="font-h3 text-dark-blue mb-2">
          À propos de {chef.firstName}
        </h2>
        {chef.specialisation && (
          <p className="text-primary font-medium italic mb-4">
            “{chef.specialisation}”
          </p>
        )}

        {chef.description && (
          <p className="text-dark-blue/80 leading-relaxed mb-5">
            {chef.description}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {bookingsCount > 0 && (
            <span>
              <strong className="text-dark-blue font-bold tabular-nums">
                {bookingsCount}
              </strong>{" "}
              prestation{bookingsCount > 1 ? "s" : ""} réalisée
              {bookingsCount > 1 ? "s" : ""}
            </span>
          )}
          {rating > 0 && (
            <>
              <span aria-hidden>·</span>
              <span>
                <strong className="text-dark-blue font-bold tabular-nums">
                  {rating.toFixed(1)}
                </strong>
                /5 sur{" "}
                <strong className="text-dark-blue font-bold tabular-nums">
                  {reviewsCount}
                </strong>{" "}
                avis
              </span>
            </>
          )}
          {chef.languages && chef.languages.length > 0 && (
            <>
              <span aria-hidden>·</span>
              <span className="capitalize">
                Parle {chef.languages.join(", ")}
              </span>
            </>
          )}
        </div>
      </motion.div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Reviews
// ─────────────────────────────────────────────────────────────────────────

type Feedback = {
  id: number;
  rating: number;
  comment: string;
  createdAt: string;
  customerFirstName: string;
};

function ChefReviewsSection({
  chef,
  rating,
  feedbacks,
}: {
  chef: any;
  rating: number;
  feedbacks: Feedback[];
}) {
  return (
    <section>
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-h3 text-dark-blue mb-1">
            Avis sur {chef.firstName}
          </h2>
          <div className="w-16 h-[2px] bg-primary rounded-full" />
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/10">
          <Star size={14} fill="currentColor" className="text-primary" />
          <span className="text-sm font-bold text-dark-blue tabular-nums">
            {rating.toFixed(1)}
          </span>
          <span className="text-xs text-muted-foreground">
            · {feedbacks.length} avis
          </span>
        </div>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        {feedbacks.slice(0, 6).map((f) => (
          <article
            key={f.id}
            className="rounded-2xl border border-border p-5 bg-gradient-to-br from-[#fbf6f3] to-white"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={13}
                    className={
                      i < f.rating
                        ? "fill-amber-400 text-amber-400"
                        : "fill-gray-100 text-gray-200"
                    }
                  />
                ))}
              </div>
              <span className="text-[10px] text-muted-foreground">
                {formatDistanceToNow(new Date(f.createdAt), {
                  locale: fr,
                  addSuffix: true,
                })}
              </span>
            </div>
            <p className="text-sm text-dark-blue leading-relaxed mb-3 italic">
              “{f.comment}”
            </p>
            <p className="text-[11px] font-semibold text-muted-foreground">
              — {f.customerFirstName}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Zone d'intervention
// ─────────────────────────────────────────────────────────────────────────

function ZoneDeplacementCard({
  firstName,
  workingCity,
}: {
  firstName: string;
  workingCity: string;
}) {
  return (
    <section className="rounded-3xl bg-dark-blue p-8 lg:p-10 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-primary/10 translate-x-1/3 -translate-y-1/3 pointer-events-none" />
      <div className="relative z-10">
        <p className="text-primary text-xs font-semibold uppercase tracking-widest mb-3">
          Zone d&apos;intervention
        </p>
        <h3 className="font-h3 text-white mb-2">
          {firstName} se déplace à {workingCity || "votre adresse"} et alentours
        </h3>
        <p className="text-white/60 text-sm max-w-md">
          Frais de déplacement inclus dans un rayon de 25 km. Au-delà, une
          participation transparente est indiquée dans le devis.
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Custom request inline
// ─────────────────────────────────────────────────────────────────────────

function CustomRequestInlineCard({
  firstName,
  onClick,
}: {
  firstName: string;
  onClick: () => void;
}) {
  return (
    <section className="rounded-3xl bg-gradient-to-br from-salmon/40 via-[#fbf6f3] to-white border border-primary/15 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5 justify-between">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">
          Une demande spéciale ?
        </p>
        <h3 className="font-h3 text-dark-blue">
          Composez un menu sur-mesure avec {firstName}
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">
          Décrivez votre événement, vos envies et contraintes. {firstName}{" "}
          revient vers vous avec une proposition personnalisée.
        </p>
      </div>
      <Button
        onClick={onClick}
        className="bg-primary hover:bg-primary/90 gap-2 shrink-0"
      >
        <Sparkles size={15} />
        Faire une demande
      </Button>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Mobile booking bar
// ─────────────────────────────────────────────────────────────────────────

function MobileBookingBar({
  firstName,
  priceFromCents,
  onClick,
}: {
  firstName: string;
  priceFromCents: number | null;
  onClick: () => void;
}) {
  return (
    <div
      className={cn(
        "lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-border px-4 py-3",
        "flex items-center justify-between gap-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]",
      )}
    >
      <div className="min-w-0">
        {priceFromCents != null ? (
          <>
            <p className="text-xs text-muted-foreground leading-none">
              À partir de
            </p>
            <p className="text-dark-blue leading-tight mt-1">
              <span className="text-xl font-bold">
                {Math.round(priceFromCents / 100)}€
              </span>
              <span className="text-xs text-muted-foreground ml-1">/pers</span>
            </p>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Sur devis</p>
        )}
      </div>
      <Button
        onClick={onClick}
        className="bg-primary hover:bg-primary/90 text-white gap-2 shadow-md shadow-primary/20 px-5 h-11"
      >
        <Sparkles size={15} />
        Réserver {firstName}
      </Button>
    </div>
  );
}
